import { ccfRoutes } from '@app/ccf/ccf-routes';
import { clientTypeOptions } from '@app/ccf/options/client-type-options';
import { useGenerateSupplyChainFlowTemplate } from '@shared/api/hooks/ccf/activities/supply-chain-logistics';
import { TypeC5bEnum, WeightOptionEnum } from '@shared/api/types';
import Text from '@shared/components/content/text';
import SelectFormField from '@shared/components/form/select-form-field';
import GuideTemplate from '@shared/components/guide-template';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useFormContext } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { useCCF } from '../../ccf-context';

export default function DistributionFlowAboutClientsPage() {
  const { trigger, getValues, reset } = useFormContext();
  const { uuid: reportUuid } = useCCF();
  const navigate = useNavigate();
  const { mutateAsync: generateTemplate, isLoading: isGeneratingTemplate } =
    useGenerateSupplyChainFlowTemplate();
  const { toast } = useToast();

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const isValid = await trigger('typeOfClients');
      const typeOfClients = getValues('typeOfClients');

      if (isValid) {
        const res = await generateTemplate({
          data: {
            type: 'distribution',
            typeOfClients: typeOfClients,
          },
        });

        reset({
          typeOfClients: typeOfClients,
          type: TypeC5bEnum['distribution'],
          weightOption: WeightOptionEnum['purchased_goods'],
          segments:
            res.segments.length > 0
              ? res.segments.map((segment) => ({
                  ...segment,
                  distance: String(segment.distance),
                }))
              : [{}],
        });
        navigate(
          generatePath(ccfRoutes.DISTRIBUTION_FLOW_REVIEW, {
            report_uuid: reportUuid,
            flow_uuid: '',
          })
        );
      }
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Error',
        description:
          'There was an error generating the distribution flow template.',
        variant: 'destructive',
      });
    }
  };

  return (
    <GuideTemplate
      preTitle="ADD DISTRIBUTION FLOW"
      title="About your clients"
      description={
        <Text>
          Start by telling us who sell your products to, we will model the
          distribution to them and on to the end consumer. If you supply
          multiple types of client, you will need to create a separate flow to
          model distribution to each client separately.
        </Text>
      }
      primaryActionText="Generate flow"
      primaryActionProps={{
        form: 'about-clients-form',
        type: 'submit',
        loading: isGeneratingTemplate,
      }}
    >
      <form id="about-clients-form" onSubmit={onSubmit}>
        <div className="flex w-full flex-col gap-6">
          <SelectFormField
            name="typeOfClients"
            label="Who do you sell your products to?"
            options={clientTypeOptions}
            placeholder="Select a type of client"
            disabled={isGeneratingTemplate}
          />
        </div>
      </form>
    </GuideTemplate>
  );
}
